/**
 * @module task.actions
 */
import { createAsyncThunk, createAction, nanoid } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import { httpRequest } from '~/adapters/httpRequest';
import { host } from '~/config/settings/worktango.settings';
import { statuses } from '~/config/settings/notification.settings';
import { urls } from '~/tools/urls';
import * as languages from '../languages';
import * as schemas from '../schemas';
import * as transformers from '../transformers';
import { Task, TaskTranslations } from '~/interfaces/action_planning.interface';
import { RootState } from '../store';
import { Notification } from '~/interfaces/employee.interface';
import { datetime } from '~/tools/datetime';
import { actionsMutations } from '../mutations/actions.mutations';
import { actionsQueries } from '../queries/actions.queries';

const ACTION_DATE_FORMAT = 'yyyy-MM-dd';

const fetchTaskList = createAsyncThunk<{ [taskId: number]: Task }, {}, { state: RootState }>(
  'action-planning/fetchTaskList',
  async (empty, { getState }) => {
    const account = getState().account;
    const taskList = await actionsQueries.fetchCompanyActionsList(account?.companyId);

    const normalizedData = normalize(taskList, [schemas.task]);

    return normalizedData;
  },
);

const fetchTask = createAsyncThunk<{ [taskId: number]: Task }, { taskId: number | null }>(
  'action-planning/fetchTask',
  async ({ taskId = null }, { rejectWithValue }) => {
    const url = `${host}/action-planning/actions/${taskId}`;
    const res = await httpRequest<any>(url);

    if (res.error) return rejectWithValue(res.error);

    const task = transformers.taskFrom(res.payload);
    const normalizedData = normalize(task, schemas.task);

    return normalizedData;
  },
);

export const createTask = createAsyncThunk<
  {
    [taskId: number]: Task;
    notification: Notification;
  },
  {
    templateId: number | null;
    surveyTypeId?: number | null;
    translations: TaskTranslations;
    dueAt: string;
    surveyIds: number[];
    categoryIds: number[];
    userIds: number[];
  }
>(
  'action-planning/createTask',
  async ({
    templateId = null,
    surveyTypeId = null,
    translations = {},
    dueAt = '',
    surveyIds = [],
    categoryIds = [],
    userIds = [],
  }) => {
    const formattedDueAt = dueAt ? datetime.format(new Date(dueAt), ACTION_DATE_FORMAT) : null;
    const task = await actionsMutations.createAction({
      libraryActionId: templateId,
      surveyTypeId,
      translations,
      dueAt: formattedDueAt,
      surveyIds,
      categoryIds,
      userId: userIds[0],
    });

    const normalizedData = normalize(task, schemas.task);

    return {
      ...normalizedData,
      notification: {
        title: languages.t('notifications.title.done'),
        message: languages.t('notifications.message.actionCreated'),
        status: statuses.success,
      },
    };
  },
);

const modifyTask = createAsyncThunk<
  {
    [taskId: number]: Task;
    notification: Notification;
  },
  {
    taskId: number | null;
    surveyTypeId: number | null;
    isComplete: boolean | null;
    translations: TaskTranslations;
    dueAt: string;
    surveyIds: number[];
    categoryIds: number[];
    userIds: number[];
  }
>(
  'action-planning/modifyTask',
  async ({
    taskId = null,
    surveyTypeId = null,
    isComplete = null,
    translations = {},
    dueAt = null,
    surveyIds = null,
    categoryIds = null,
    userIds = null,
  }) => {
    const url = `${host}/action-planning/actions/${taskId}`;
    const formattedDueAt = dueAt ? datetime.format(new Date(dueAt), ACTION_DATE_FORMAT) : null;
    const res = await httpRequest<any>(url, {
      method: 'PATCH',
      body: {
        surveyTypeId,
        isComplete,
        translations,
        dueAt: formattedDueAt,
        surveyIds,
        categoryIds,
        userIds,
      },
    });

    const task = transformers.taskFrom(res.payload);
    const normalizedData = normalize(task, schemas.task);

    return {
      ...normalizedData,
      notification: {
        title: languages.t('notifications.title.done'),
        message: languages.t('notifications.message.actionUpdated'),
        status: statuses.success,
      },
    };
  },
);

/**
 * Used to toggle `isComplete` for task with matching `id` in front-end state.
 *
 * NOTE: This does NOT update/sync with the server. Only use this action to make
 * a quick, immediate, front-end UI change along with a corresponding
 * `modifyTask()` action call.
 */
const toggleTask = createAction('action-planning/toggleTask', (taskId) => ({
  payload: taskId,
}));

const downloadTaskListCSV = createAsyncThunk<
  boolean,
  {
    isComplete: boolean | null;
    userIds: number[] | null;
    surveyIds: number[] | null;
    categoryIds: number[] | null;
  }
>(
  'action-planning/downloadTaskListCSV',
  async ({ isComplete = null, userIds = null, surveyIds = null, categoryIds = null }) => {
    // Set filters specifically to `undefined` if they are `null` so that the
    // backend will ignore them when they aren't being used.
    // Example: `isComplete=false&surveyIds=1,2,4&userIds=1,2,3&categoryIds=1,2,3&queryId=4B23ssd8`
    const queryParams = urls.queryStringify({
      isComplete: isComplete === null ? undefined : isComplete,
      userIds: userIds || undefined,
      surveyIds: surveyIds || undefined,
      categoryIds: categoryIds || undefined,
      queryId: nanoid(8), // This is not used for anything other than to append a random value to the url to prevent cacheing.
    });

    const url = `${host}/action-planning/actions.csv?${queryParams}`;
    const res = await httpRequest<any>(url, {
      headers: {
        Accept: 'text/csv',
      },
    });

    // Temporarily create a new <a> tag in the DOM, append the raw data blob to
    // it, assign a download filename, click the link to trigger the download,
    // and remove it from the DOM when done.
    const blob = await res.blob();
    const link = document.createElement('a');
    const now = datetime.format(new Date(), ACTION_DATE_FORMAT);
    const filename = `worktango_actions_${now}.csv`;

    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return true;
  },
);

const removeTask = createAsyncThunk<{ id: number; notification: Notification } | unknown, { taskId: number }>(
  'action-planning/removeTask',
  async (taskId, { rejectWithValue }) => {
    const url = `${host}/action-planning/actions/${taskId}`;
    const res = await httpRequest<any>(url, {
      method: 'DELETE',
    });

    if (res.error) return rejectWithValue(res.error);

    return {
      id: taskId,
      notification: {
        title: languages.t('notifications.title.done'),
        message: languages.t('notifications.message.actionRemoved'),
        status: statuses.success,
      },
    };
  },
);

export const taskActions = {
  fetchTaskList,
  fetchTask,
  createTask,
  modifyTask,
  removeTask,
  toggleTask,
  downloadTaskListCSV,
};
